import React, { useState, useEffect, useContext, useRef } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsMore from 'highcharts/highcharts-more';
import { Collapse } from 'antd';

const QQPlotChart = (props) => {
    const { Panel } = Collapse;

    // Support both data sources: regular qq_plot and standardized_residuals_qq_plot
    let qqPlotData = props.getAnalysisRes.qq_plot || props.getAnalysisRes.standardized_residuals_qq_plot;
    let isStandardizedResiduals = !!props.getAnalysisRes.standardized_residuals_qq_plot;
    

    let sampleQuantiles = qqPlotData.chart.sample_quantiles;
    let normalTheoreticalQuantiles = qqPlotData.chart.normal_theoretical_quantiles;
    let lognormalTheoreticalQuantiles = qqPlotData.chart.lognormal_theoretical_quantiles;
    let burr12TheoreticalQuantiles = qqPlotData.chart.burr12_theoretical_quantiles;

    let dotplotDisplayName = props.getAnalysisRes.display_name;

    // Create scatter plot data pairs for each distribution
    let normalData = [];
    let lognormalData = [];
    let burr12Data = [];

    if (sampleQuantiles && normalTheoreticalQuantiles && sampleQuantiles.length === normalTheoreticalQuantiles.length) {
        normalData = sampleQuantiles.map((sample, index) => [normalTheoreticalQuantiles[index], sample]);
    }

    if (sampleQuantiles && lognormalTheoreticalQuantiles && sampleQuantiles.length === lognormalTheoreticalQuantiles.length) {
        lognormalData = sampleQuantiles.map((sample, index) => [lognormalTheoreticalQuantiles[index], sample]);
    }

    if (sampleQuantiles && burr12TheoreticalQuantiles && sampleQuantiles.length === burr12TheoreticalQuantiles.length) {
        burr12Data = sampleQuantiles.map((sample, index) => [burr12TheoreticalQuantiles[index], sample]);
    }

    // Calculate perfect fit lines for each distribution
    let normalPerfectFitLine = [];
    let lognormalPerfectFitLine = [];
    let burr12PerfectFitLine = [];

    if (normalTheoreticalQuantiles && normalTheoreticalQuantiles.length > 0) {
        let minNormal = Math.min(...normalTheoreticalQuantiles);
        let maxNormal = Math.max(...normalTheoreticalQuantiles);
        normalPerfectFitLine = [[minNormal, minNormal], [maxNormal, maxNormal]];
    }

    if (lognormalTheoreticalQuantiles && lognormalTheoreticalQuantiles.length > 0) {
        let minLognormal = Math.min(...lognormalTheoreticalQuantiles);
        let maxLognormal = Math.max(...lognormalTheoreticalQuantiles);
        lognormalPerfectFitLine = [[minLognormal, minLognormal], [maxLognormal, maxLognormal]];
    }

    if (burr12TheoreticalQuantiles && burr12TheoreticalQuantiles.length > 0) {
        let minBurr12 = Math.min(...burr12TheoreticalQuantiles);
        let maxBurr12 = Math.max(...burr12TheoreticalQuantiles);
        burr12PerfectFitLine = [[minBurr12, minBurr12], [maxBurr12, maxBurr12]];
    }

    // Create chart options for each distribution
    const createChartOptions = (title, data, perfectFitLine, color, symbol) => ({
        credits: {
            enabled: false
        },
        chart: {
            renderTo: `chart-container-qq-${title.toLowerCase()}`,
            type: 'scatter',
            height: 'auto',
            // width: 'auto'
        },
        boost: {
            useGPUTranslations: true,
            usePreAllocated: true
        },
        title: { 
            text: isStandardizedResiduals ? `${title} Q-Q Plot of Standardized Residuals` : `${title} Q-Q Plot`, 
            style: { fontSize: '12px' } 
        },
        subtitle: { text: '', style: { fontSize: '10px' } },
        xAxis: {
            title: {
                text: 'Theoretical Quantiles',
                style: {
                    fontWeight: 'normal',
                    fontSize: '10px'
                }
            },
            labels: { 
                style: { fontSize: '9px' }
            }
        },
        yAxis: {
            title: {
                text: isStandardizedResiduals ? 'Standardized Residuals Quantiles' : 'Dependent Variable Quantiles',
                style: {
                    fontWeight: 'normal',
                    fontSize: '10px'
                }
            },
            labels: {
                style: {
                    fontSize: '9px',
                    color: '#6D869F'
                }
            }
        },
        plotOptions: {
            series: { 
                animation: true,
                marker: {
                    radius: 3
                }
            },
            scatter: { 
                animation: true,
                marker: {
                    radius: 3
                }
            }
        },
        tooltip: {
            formatter: function () {
                return `Theoretical: ${this.x.toFixed(4)}<br/>Sample: ${this.y.toFixed(4)}`;
            }
        },
        legend: {
            enabled: false
        },
        series: [
            {
                name: 'Perfect Fit (y=x)',
                type: 'line',
                color: '#000000',
                data: perfectFitLine,
                marker: { enabled: false },
                lineWidth: 1,
                dashStyle: 'dash',
                zIndex: 1
            },
            {
                name: `${title} Q-Q`,
                type: 'scatter',
                color: color,
                data: data,
                marker: {
                    symbol: symbol,
                    radius: 3
                },
                zIndex: 2
            }
        ]
    });

    const normalOptions = createChartOptions('Normal', normalData, normalPerfectFitLine, '#60091d', 'circle');
    const lognormalOptions = createChartOptions('Lognormal', lognormalData, lognormalPerfectFitLine, '#76ac54', 'circle');
    const burr12Options = createChartOptions('Burr12', burr12Data, burr12PerfectFitLine, '#cb2e26', 'circle');

    return (
        <>
            <Collapse bordered={false} defaultActiveKey={[1]}>
                <Panel key={1} header={`${isStandardizedResiduals ? 'Standardized Residuals Q-Q Plots' : 'Q-Q Plots'} ${props.getAnalysisRes.filter_by_variable == null ? '': `[${props.getAnalysisRes.filter_by_variable}]`}`}>
                    {}
                    
                    {/* Normal Q-Q Plot */}
                    {normalData.length > 0 && (
                        <div style={{ marginBottom: '20px' }}>
                            <HighchartsReact
                                highcharts={Highcharts}
                                options={normalOptions}
                                ref={props.forwardedRefNormal}
                            />
                        </div>
                    )}
                    
                    {/* Lognormal Q-Q Plot */}
                    {lognormalData.length > 0 && (
                        <div style={{ marginBottom: '20px' }}>
                            <HighchartsReact
                                highcharts={Highcharts}
                                options={lognormalOptions}
                                ref={props.forwardedRefLognormal}
                            />
                        </div>
                    )}
                    
                    {/* Burr12 Q-Q Plot */}
                    {burr12Data.length > 0 && (
                        <div style={{ marginBottom: '20px' }}>
                            <HighchartsReact
                                highcharts={Highcharts}
                                options={burr12Options}
                                ref={props.forwardedRefBurr12}
                            />
                        </div>
                    )}
                </Panel>
            </Collapse>
        </>
    );
};

export default React.memo(QQPlotChart);
